import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  use,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import brLocale from 'date-fns/locale/pt-BR';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { Box, Chip } from "@material-ui/core";
import { TagsFilter } from "../../components/TagsFilter";
import { UsersFilter } from "../../components/UsersFilter";
import { Stack } from "@mui/material";
import useTickets from "../../hooks/useTickets";
import ContactTag from "../../components/ContactTag";
import { format, parseISO, subDays } from "date-fns";
import { Link } from "react-router-dom";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";


const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const tickets = action.payload;
    const newTickets = [];

    tickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
      } else {
        newTickets.push(ticket);
      }
    });
    return [...state, ...newTickets];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    maxWidth: "100vw",
    maxHeight: "100vh",
  },
}));

const TagsReport = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [initialDate, setInitialDate] = useState(subDays(new Date(), 30));
  const [finalDate, setFinalDate] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [selectedTags, selectedUsers, initialDate, finalDate]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTickets = async () => {
        try {
          const { data } = await api.get("/ticketsreport", {
            params: {
              pageNumber,
              tags: JSON.stringify(selectedTags),
              users: JSON.stringify(selectedUsers),
              date: initialDate,
              finalDate
            },
          });
          dispatch({ type: "LOAD_TICKETS", payload: data.tickets });
          setHasMore(data.hasMore);
          setLoading(false);

        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchTickets();
    }, 500); // Delay de 500ms
    return () => clearTimeout(delayDebounceFn); // Limpa o timeout se o componente for desmontado ou se a dependência mudar
  }, [pageNumber, selectedTags, selectedUsers, initialDate, finalDate]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  return (
    <MainContainer>
      <MainHeader >
        <Title >Relatório de tags</Title>
        <MainHeaderButtonsWrapper>
          <Stack direction={{ xs: "column", sm: "row" }} // Muda para coluna no breakpoint sm
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              maxWidth: "100vw",
              flex: "auto",
              width: "100%", // Ocupa 100% da largura do componente pai
            }}>
            <TagsFilter onFiltered={handleSelectedTags} style={{ widht: '100%' }} />
            <UsersFilter onFiltered={handleSelectedUsers} style={{ widht: '100%' }} />
            <Box style={{ padding: '5px', height: '66px', alignItems: 'center', display: 'flex', justifyContent: 'center', flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                <DatePicker
                  value={initialDate}
                  onChange={(newValue) => { setInitialDate(newValue) }}
                  label="Data inicial"
                  renderInput={(params) => <TextField variant="outlined" size="small" fullWidth {...params} sx={{ width: '20ch', height: '100%' }} />}

                />
              </LocalizationProvider>
            </Box>
            <Box style={{ padding: '5px', height: '66px', alignItems: 'center', display: 'flex', justifyContent: 'center', flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                <DatePicker
                  value={finalDate}
                  onChange={(newValue) => { setFinalDate(newValue) }}
                  label="Data final"
                  renderInput={(params) => <TextField
                    variant="outlined"
                    size="small"
                    fullWidth {...params} sx={{ width: '20ch', height: '100%' }} />}
                />
              </LocalizationProvider>
            </Box>

          </Stack>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Ticket id</TableCell>
              <TableCell align="center">Ticket status</TableCell>
              <TableCell align="center">Contato</TableCell>
              <TableCell align="center">Atendente</TableCell>
              <TableCell align="center">Tag</TableCell>
              <TableCell align="center">Data atendimento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {/* Alterei o estado para 'tickets' em vez de 'ticketsList' */}
              {ticketsList.map((ticket) => {
                const { contact, tags, user } = ticket;
                return (
                  <TableRow key={ticket.id}>
                    <TableCell align="center">
                      <Link to={`/tickets/${ticket?.id}`}>{ticket?.id}</Link>
                    </TableCell>
                    <TableCell align="center">{ticket?.status}</TableCell>
                    <TableCell align="center">{contact?.name}</TableCell>
                    <TableCell align="center">{user?.name}</TableCell>
                    <TableCell align="center">
                      {tags.map((tag) => (
                        <ContactTag tag={tag} key={tag?.id} />
                      ))}
                    </TableCell>
                    <TableCell align="center"> {format(parseISO(ticket?.createdAt), "dd/MM/yyyy")}</TableCell>
                    {/* <TableCell align="center">
                      <Chip
                        variant="outlined"
                        style={{
                          backgroundColor: tag.color,
                          textShadow: "1px 1px 1px #000",
                          color: "white",
                        }}
                        label={tag.name}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">{tag.ticketsCount}</TableCell>
                    <TableCell align="center">{tag.ticketsCount}</TableCell>
                    <TableCell align="center">{tag.ticketsCount}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" >
                        <EditIcon />
                      </IconButton>
  
                      <IconButton
                        size="small"
  
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell> */}
                  </TableRow>
                )
              })}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TagsReport;
